import { useCallback, useEffect, useRef, useState } from "react";

function readPosition(storageKey) {
  try {
    return JSON.parse(window.sessionStorage.getItem(storageKey)) || null;
  } catch {
    return null;
  }
}

function writePosition(storageKey, snapshot) {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    // The log remains usable when browser storage is unavailable.
  }
}

function capturePosition(api) {
  const top = api.getVerticalPixelRange().top;
  const left = api.getHorizontalPixelRange().left;
  const rowCount = api.getDisplayedRowCount();
  let index = Math.max(api.getFirstDisplayedRowIndex(), 0);
  let row = api.getDisplayedRowAtIndex(index);

  // AG Grid's first displayed index includes buffered rows above the viewport.
  while (row && index < rowCount - 1 && row.rowTop + row.rowHeight <= top) {
    row = api.getDisplayedRowAtIndex(++index);
  }

  return {
    top,
    left,
    rowId: row?.id,
    rowOffset: row ? top - row.rowTop : 0,
  };
}

// Remembers the visible section and the controls that determine its rows.
export function useProcurementLogPosition({
  storageKey,
  ready,
  gridRef,
  rows,
  viewState,
  onRestoreViewState,
}) {
  const loadedKeyRef = useRef(null);
  const positionRef = useRef({ top: 0, left: 0 });
  const restoringRef = useRef(true);
  const saveTimeoutRef = useRef(null);
  const latestRef = useRef({ storageKey, ready, viewState });
  const [restoreRevision, setRestoreRevision] = useState(0);

  useEffect(() => {
    latestRef.current = { storageKey, ready, viewState };
  }, [storageKey, ready, viewState]);

  const savePosition = useCallback(() => {
    const latest = latestRef.current;
    if (
      !latest.ready ||
      restoringRef.current ||
      loadedKeyRef.current !== latest.storageKey
    ) {
      return;
    }

    writePosition(latest.storageKey, {
      position: positionRef.current,
      viewState: latest.viewState,
    });
  }, []);

  useEffect(() => {
    if (!ready) {
      restoringRef.current = true;
      return;
    }

    if (loadedKeyRef.current !== storageKey) {
      const snapshot = readPosition(storageKey);
      loadedKeyRef.current = storageKey;
      positionRef.current = snapshot?.position || { top: 0, left: 0 };
      onRestoreViewState(snapshot?.viewState || {});
    }

    restoringRef.current = true;
    setRestoreRevision((revision) => revision + 1);
  }, [onRestoreViewState, ready, storageKey]);

  useEffect(() => {
    if (!ready || !restoreRevision || !restoringRef.current) {
      return;
    }

    let frame;
    frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => {
        const api = gridRef.current?.api;
        if (!api || api.isDestroyed()) {
          if (!rows.length) {
            restoringRef.current = false;
            savePosition();
          }
          return;
        }

        // Wait for the saved expansions and filters to reach the grid model.
        if (
          api.getDisplayedRowCount() !== rows.length ||
          (rows.length &&
            api.getDisplayedRowAtIndex(rows.length - 1)?.id !==
              rows[rows.length - 1]?.id)
        ) {
          return;
        }

        const position = positionRef.current;
        const anchor = position.rowId ? api.getRowNode(position.rowId) : null;
        if (anchor) {
          // Resolve estimated row heights before restoring the offset within it.
          api.ensureNodeVisible(anchor, "top");
        }
        const top = anchor
          ? Math.max(0, anchor.rowTop + (position.rowOffset || 0))
          : Math.max(0, position.top || 0);

        const gridState = api.getState();
        api.setState(
          {
            ...gridState,
            scroll: { top, left: Math.max(0, position.left || 0) },
          },
          Object.keys(gridState).filter(
            (property) => property !== "scroll" && property !== "version",
          ),
        );

        frame = window.requestAnimationFrame(() => {
          if (api.isDestroyed()) return;
          positionRef.current = capturePosition(api);
          restoringRef.current = false;
          savePosition();
        });
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [gridRef, ready, restoreRevision, rows, savePosition, viewState]);

  useEffect(() => {
    savePosition();
  }, [savePosition, viewState]);

  const handleBodyScroll = useCallback(
    ({ api }) => {
      if (restoringRef.current || !latestRef.current.ready) return;
      positionRef.current = capturePosition(api);
      window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = window.setTimeout(savePosition, 150);
    },
    [savePosition],
  );

  const handleGridRowsReady = useCallback(() => {
    if (restoringRef.current && latestRef.current.ready) {
      setRestoreRevision((revision) => revision + 1);
    }
  }, []);

  const handleGridPreDestroyed = useCallback(
    ({ api }) => {
      if (!restoringRef.current) {
        positionRef.current = capturePosition(api);
        savePosition();
      }
    },
    [savePosition],
  );

  useEffect(() => {
    const flushPosition = () => {
      const api = gridRef.current?.api;
      if (api && !api.isDestroyed() && !restoringRef.current) {
        positionRef.current = capturePosition(api);
      }
      savePosition();
    };

    window.addEventListener("pagehide", flushPosition);
    window.addEventListener("beforeunload", flushPosition);
    return () => {
      window.clearTimeout(saveTimeoutRef.current);
      flushPosition();
      window.removeEventListener("pagehide", flushPosition);
      window.removeEventListener("beforeunload", flushPosition);
    };
  }, [gridRef, savePosition]);

  return { handleBodyScroll, handleGridRowsReady, handleGridPreDestroyed };
}
